import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Building2 } from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Scaffolding & Shuttering Services | Amar Preet";
    axios.get(`${API}/services`)
      .then(res => setServices(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="services-page">
      <SEO title="Our Services" description="Scaffolding rental, shuttering material, cuplock scaffolding, acrospan props & industrial scaffolding services. Pan-India delivery from Delhi, Noida, Hyderabad, Vizag." canonical="/services" />
      {/* Hero */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-yellow-500 font-semibold text-sm tracking-wider uppercase">Our Services</span>
          <h1 className="text-5xl md:text-6xl font-bold mt-4 mb-6" data-testid="services-headline">
            Professional Scaffolding Solutions
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Latest technology, machines & equipment for all types of construction projects
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/${service.slug}`}
                className="group bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-transparent hover:border-yellow-500"
                data-testid={`service-${service.slug}`}
              >
                <div className="w-20 h-20 bg-yellow-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform overflow-hidden">
                  {service.image ? (
                    <img src={service.image} alt={service.name} className={`w-full h-full object-contain ${service.image_style === 'light' ? 'mix-blend-multiply' : 'invert mix-blend-multiply'}`} />
                  ) : (
                    <Building2 className="w-8 h-8 text-slate-900" />
                  )}
                </div>
                <span className="text-xs font-semibold text-yellow-600 uppercase tracking-wider">{service.category}</span>
                <h2 className="text-2xl font-bold text-slate-900 mt-2 mb-4">{service.name}</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">{service.short_description}</p>
                <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wider mb-4">Key Features:</h4>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="text-sm text-slate-600 flex items-center">
                      <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center text-yellow-600 font-semibold group-hover:text-yellow-700 transition-colors">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Additional Services</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Beyond equipment rental, we provide comprehensive support for your construction needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Site Survey", description: "Free site assessment and quantity estimation" },
              { title: "Layout Planning", description: "Technical support for scaffolding layout" },
              { title: "Installation Guidance", description: "Expert supervision on request" },
              { title: "Timely Delivery", description: "Multi-city inventory for quick mobilization" }
            ].map((service) => (
              <div key={service.title} className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="font-bold text-slate-900 mb-2">{service.title}</h3>
                <p className="text-sm text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Need Scaffolding for Your Project?
          </h2>
          <p className="text-slate-700 mb-8 max-w-xl mx-auto">
            Get a customized quote based on your project requirements
          </p>
          <Link
            to="/contact"
            className="inline-block bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 hover:scale-105"
            data-testid="services-cta"
          >
            Get Free Quote
          </Link>
        </div>
      </section>
    </div>
  );
}
